import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useLoginMutation, useRegisterMutation } from '../store/slices/apiSlice';
import { setCredentials } from '../store/slices/authSlice';
import { Toast } from '../components/Toast';

function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLogin, setIsLogin] = useState(true);
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [register, { isLoading: isRegisterLoading }] = useRegisterMutation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [apiError, setApiError] = useState('');

  // Reset form when switching between login/register
  useEffect(() => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      displayName: ''
    });
    setErrors({});
    setTouched({});
    setPasswordStrength(0);
    setApiError('');
  }, [isLogin]);

  // Validate email
  const validateEmail = (email) => {
    if (!email) return 'Vui lòng nhập email';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Email không hợp lệ';
    return '';
  };

  // Validate display name
  const validateDisplayName = (displayName) => {
    if (!displayName) return 'Vui lòng nhập tên hiển thị';
    if (displayName.length < 2) return 'Tên hiển thị phải có ít nhất 2 ký tự';
    if (displayName.length > 50) return 'Tên hiển thị không được quá 50 ký tự';
    return '';
  };

  // Validate password
  const validatePassword = (password) => {
    if (!password) return 'Vui lòng nhập mật khẩu';
    if (password.length < 6) return 'Mật khẩu phải có ít nhất 6 ký tự';
    if (password.length > 50) return 'Mật khẩu không được quá 50 ký tự';
    return '';
  };

  // Validate confirm password
  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return 'Vui lòng xác nhận mật khẩu';
    if (confirmPassword !== password) return 'Mật khẩu xác nhận không khớp';
    return '';
  };

  // Calculate password strength
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
    return Math.min(strength, 100);
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Calculate password strength
    if (name === 'password' && !isLogin) {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Real-time validation
    if (touched[name]) {
      validateField(name, value);
    }
  };

  // Handle blur
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({ ...touched, [name]: true });
    validateField(name, value);
  };

  // Validate single field
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'displayName':
        error = validateDisplayName(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'confirmPassword':
        error = validateConfirmPassword(value, formData.password);
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    newErrors.email = validateEmail(formData.email);
    newErrors.password = validatePassword(formData.password);

    if (!isLogin) {
      newErrors.displayName = validateDisplayName(formData.displayName);
      newErrors.confirmPassword = validateConfirmPassword(formData.confirmPassword, formData.password);
    }

    setErrors(newErrors);
    setTouched({
      email: true,
      password: true,
      confirmPassword: true,
      displayName: true
    });

    return Object.values(newErrors).every(error => !error);
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous API error
    setApiError('');

    // Validate form immediately
    if (!validateForm()) {
      setApiError('Vui lòng kiểm tra lại thông tin!');
      Toast.error('Vui lòng kiểm tra lại thông tin!');
      return;
    }

    try {
      if (isLogin) {
        const result = await login({
          identifier: formData.email.trim(),
          password: formData.password
        }).unwrap();
        
        dispatch(setCredentials({ user: result, token: result.token }));
        Toast.success('Đăng nhập thành công!');
        
        // Delay navigation for better UX
        setTimeout(() => {
          // Nếu chưa hoàn thành onboarding thì redirect đến onboarding, ngược lại về trang chủ
          if (!result.onboardingCompleted) {
            navigate('/onboarding');
          } else {
            navigate('/');
          }
        }, 500);
      } else {
        const result = await register({
          email: formData.email.trim(),
          password: formData.password,
          displayName: formData.displayName.trim()
        }).unwrap();
        
        dispatch(setCredentials({ user: result, token: result.token }));
        Toast.success('Đăng ký thành công!');
        
        // Redirect về trang chủ luôn, không bắt buộc onboarding
        setTimeout(() => {
          navigate('/');
        }, 500);
      }
    } catch (error) {
      console.log('Login/Register error:', error);
      
      // Handle different error types - show immediately
      let errorMessage = 'Có lỗi xảy ra, vui lòng thử lại!';
      
      if (error?.status === 401) {
        errorMessage = 'Email hoặc mật khẩu không đúng!';
      } else if (error?.status === 409) {
        errorMessage = 'Email đã tồn tại!';
      } else if (error?.status === 400) {
        errorMessage = error?.data?.error || error?.data?.message || 'Thông tin không hợp lệ!';
      } else if (error?.status === 500) {
        errorMessage = 'Lỗi máy chủ, vui lòng thử lại sau!';
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Show error immediately in form and toast
      setApiError(errorMessage);
      Toast.error(errorMessage);
    }
  };

  const isLoading = isLoginLoading || isRegisterLoading;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in-up">
          <Link to="/" className="inline-block">
            <img 
              src="https://res.cloudinary.com/ds52btbjy/image/upload/v1772812120/logo_full_hcwxaa.png" 
              alt="Logo" 
              className="w-16 h-16 mx-auto mb-4 hover:scale-110 transition-transform duration-300" 
            />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {isLogin ? 'Chào mừng trở lại!' : 'Tạo tài khoản mới'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isLogin ? 'Đăng nhập để tiếp tục' : 'Đăng ký để bắt đầu'}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 animate-scale-in">
          {/* API Error Alert */}
          {apiError && (
            <div className="mb-5 p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-lg animate-slide-down">
              <div className="flex items-start gap-3">
                <span className="material-symbols-rounded text-red-600 text-xl mt-0.5">error</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">Đã có lỗi xảy ra</p>
                  <p className="text-sm text-red-700 dark:text-red-300">{apiError}</p>
                </div>
                <button
                  onClick={() => setApiError('')}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                >
                  <span className="material-symbols-rounded text-lg">close</span>
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="animate-fade-in-up stagger-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-rounded text-gray-400 text-[20px]">
                  email
                </span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoading}
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl transition-all focus:outline-none ${
                    errors.email && touched.email
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-200 dark:border-gray-600 focus:border-primary-500'
                  } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                  placeholder="Nhập email của bạn"
                />
              </div>
              {errors.email && touched.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1 animate-slide-down">
                  <span className="material-symbols-rounded text-[16px]">error</span>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Display Name - Register only */}
            {!isLogin && (
              <div className="animate-fade-in-up stagger-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Tên hiển thị
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-rounded text-gray-400 text-[20px]">
                    badge
                  </span>
                  <input
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl transition-all focus:outline-none ${
                      errors.displayName && touched.displayName
                        ? 'border-red-500 focus:border-red-600'
                        : 'border-gray-200 dark:border-gray-600 focus:border-primary-500'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                    placeholder="Nhập tên hiển thị"
                  />
                </div>
                {errors.displayName && touched.displayName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1 animate-slide-down">
                    <span className="material-symbols-rounded text-[16px]">error</span>
                    {errors.displayName}
                  </p>
                )}
              </div>
            )}

            {/* Password */}
            <div className={`animate-fade-in-up ${!isLogin ? 'stagger-3' : 'stagger-2'}`}>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Mật khẩu
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-rounded text-gray-400 text-[20px]">
                  lock
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoading}
                  className={`w-full pl-11 pr-12 py-3 border-2 rounded-xl transition-all focus:outline-none ${
                    errors.password && touched.password
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-200 dark:border-gray-600 focus:border-primary-500'
                  } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                  placeholder="Nhập mật khẩu"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <span className="material-symbols-rounded text-[20px]">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {errors.password && touched.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1 animate-slide-down">
                  <span className="material-symbols-rounded text-[16px]">error</span>
                  {errors.password}
                </p>
              )}
              
              {/* Password Strength - Register only */}
              {!isLogin && formData.password && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          passwordStrength < 40
                            ? 'bg-red-500'
                            : passwordStrength < 70
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${passwordStrength}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      {passwordStrength < 40 ? 'Yếu' : passwordStrength < 70 ? 'Trung bình' : 'Mạnh'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password - Register only */}
            {!isLogin && (
              <div className="animate-fade-in-up stagger-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Xác nhận mật khẩu
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-rounded text-gray-400 text-[20px]">
                    lock_reset
                  </span>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className={`w-full pl-11 pr-12 py-3 border-2 rounded-xl transition-all focus:outline-none ${
                      errors.confirmPassword && touched.confirmPassword
                        ? 'border-red-500 focus:border-red-600'
                        : 'border-gray-200 dark:border-gray-600 focus:border-primary-500'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                    placeholder="Nhập lại mật khẩu"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <span className="material-symbols-rounded text-[20px]">
                      {showConfirmPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                {errors.confirmPassword && touched.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1 animate-slide-down">
                    <span className="material-symbols-rounded text-[16px]">error</span>
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 rounded-xl font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2 ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:scale-105 shadow-lg hover:shadow-xl btn-ripple animate-gradient'
              } animate-fade-in-up stagger-5`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Đang xử lý...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-rounded text-[20px]">
                    {isLogin ? 'login' : 'person_add'}
                  </span>
                  <span>{isLogin ? 'Đăng nhập' : 'Đăng ký'}</span>
                </>
              )}
            </button>

            {/* Forgot Password Link */}
            {isLogin && (
              <div className="text-right mt-3">
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
                >
                  Quên mật khẩu?
                </Link>
              </div>
            )}
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-gray-800 text-gray-500">hoặc</span>
            </div>
          </div>

          {/* Google Login Button */}
          <a
            href={`${import.meta.env.VITE_API_URL}/auth/google`}
            className="w-full py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-lg text-gray-700 dark:text-gray-200 hover:scale-105 animate-fade-in-up stagger-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Đăng nhập với Google</span>
          </a>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-gray-800 text-gray-500">hoặc</span>
            </div>
          </div>

          {/* Switch Mode */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              disabled={isLoading}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLogin ? (
                <>Chưa có tài khoản? <span className="underline">Đăng ký ngay</span></>
              ) : (
                <>Đã có tài khoản? <span className="underline">Đăng nhập</span></>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400 animate-fade-in">
          <p>
            Bằng việc tiếp tục, bạn đồng ý với{' '}
            <Link to="/privacy-policy" className="text-primary-600 hover:underline">
              Điều khoản dịch vụ
            </Link>{' '}
            của chúng tôi
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
